import { extractErrorMessage } from "@api/axios";
import { createEntry, fetchEntries } from "@/store/entriesSlice";
import { fetchCategories } from "@/store/categoriesSlice";
import { fetchSubcategories } from "@/store/subcategoriesSlice";
import type { AppDispatch } from "@/store";
import type { Category, EntryKind, Subcategory } from "@/types";
import { parseCsv } from "./csvParse";
import { parseCsvEntryKind } from "./csvEntryKind";
import { TRACKER_CSV_COLUMNS, trackerCsvHeadersMatch } from "./trackerCsv";

export interface TrackerCsvImportResult {
  created: number;
  skipped: number;
  errors: string[];
  fatalReason: "none" | "bad_header" | "empty_file";
}

function lookupKey(kind: EntryKind, name: string): string {
  return `${kind}\x00${name.trim()}`;
}

function normalizeSpentDate(raw: string): string | undefined {
  const t = raw.trim();
  if (!t) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  if (t.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
  return undefined;
}

function buildCategoryLookup(categories: Category[]): Map<string, Category> {
  const m = new Map<string, Category>();
  for (const c of categories) {
    const kind = (c.kind ?? "expense") as EntryKind;
    m.set(lookupKey(kind, c.name), c);
  }
  return m;
}

export async function runTrackerCsvImport(
  dispatch: AppDispatch,
  csvText: string,
): Promise<TrackerCsvImportResult> {
  const fatal = (
    skipped: number,
    errors: string[],
    reason: TrackerCsvImportResult["fatalReason"],
  ): TrackerCsvImportResult => ({
    created: 0,
    skipped,
    errors,
    fatalReason: reason,
  });

  if (csvText.trim() === "") return fatal(0, [], "empty_file");

  const rows = parseCsv(csvText);

  if (rows.length === 0) return fatal(0, [], "empty_file");

  const header = rows[0]!;
  if (!trackerCsvHeadersMatch(header)) {
    return fatal(0, [], "bad_header");
  }

  const categoriesList = await dispatch(fetchCategories()).unwrap();
  const subcategoriesList = await dispatch(fetchSubcategories()).unwrap();
  const catByKey = buildCategoryLookup(categoriesList);

  const findCategory = (
    kind: EntryKind,
    exportedName: string,
  ): Category | undefined => catByKey.get(lookupKey(kind, exportedName));

  const findSubcategory = (
    categoryId: string,
    exportedName: string,
  ): Subcategory | undefined => {
    const n = exportedName.trim();
    if (!n) return undefined;
    return subcategoriesList.find(
      (s) => s.categoryId === categoryId && s.name.trim() === n,
    );
  };

  let created = 0;
  let skipped = 0;
  const errors: string[] = [];
  const nCols = TRACKER_CSV_COLUMNS.length;

  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i]!;
    const lineNo = i + 1;
    if (cells.length !== nCols) {
      skipped++;
      errors.push(`${lineNo}: expected ${nCols} columns, got ${cells.length}.`);
      continue;
    }

    const kindRaw = cells[0]!;
    const entryName = cells[1]!;
    const description = cells[2]!;
    const categoryNameRaw = cells[3]!;
    const subcategoryNameRaw = cells[4]!;
    const amountRaw = cells[5]!;
    const currencyRaw = cells[6]!;
    const dateRaw = cells[7]!;

    const kind = parseCsvEntryKind(kindRaw);
    if (!kind) {
      skipped++;
      errors.push(`${lineNo}: invalid kind "${kindRaw}".`);
      continue;
    }

    if (!categoryNameRaw.trim()) {
      skipped++;
      errors.push(`${lineNo}: missing category name.`);
      continue;
    }

    const category = findCategory(kind, categoryNameRaw);
    if (!category) {
      skipped++;
      errors.push(
        `${lineNo}: no ${kind} category "${categoryNameRaw.trim()}".`,
      );
      continue;
    }

    const subTrim = subcategoryNameRaw.trim();
    let subcategoryId: string | undefined;
    if (subTrim) {
      const sub = findSubcategory(category.id, subTrim);
      if (!sub) {
        skipped++;
        errors.push(
          `${lineNo}: subcategory "${subTrim}" not found under "${category.name.trim()}".`,
        );
        continue;
      }
      subcategoryId = sub.id;
    }

    const amount = Number(String(amountRaw).replace(",", "."));
    if (!(amount > 0) || Number.isNaN(amount)) {
      skipped++;
      errors.push(`${lineNo}: invalid amount "${amountRaw}".`);
      continue;
    }

    const spentAt = normalizeSpentDate(dateRaw);
    if (!spentAt) {
      skipped++;
      errors.push(`${lineNo}: invalid date "${dateRaw}".`);
      continue;
    }

    let currency = (currencyRaw || "EUR").trim().toUpperCase();
    if (currency.length !== 3) currency = "EUR";

    const trimmedName = entryName.trim();
    try {
      await dispatch(
        createEntry({
          name: trimmedName === "" ? null : trimmedName,
          description: description.trim() ? description.trim() : undefined,
          categoryId: category.id,
          subcategoryId,
          amount,
          currency,
          spentAt,
        }),
      ).unwrap();
      created++;
    } catch (err: unknown) {
      skipped++;
      errors.push(`${lineNo}: ${extractErrorMessage(err)}`);
    }
  }

  await dispatch(fetchEntries()).unwrap();

  return { created, skipped, errors, fatalReason: "none" };
}
